import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Grid,
    TablePagination,
    Typography,
    Stack,
    Chip,
    Tabs,
    Tab
} from "@mui/material";
import { getPublishedArticles } from "../../Services/ApiCalls";
import type { Article } from "../../types";
import Seo from "../../Components/Seo";
import ResourceLearningSummary from "../../Components/ResourceLearningSummary";

type ContentType = 'all' | 'articles' | 'blogs';

/**
 * Determines if an article is a blog based on the excerpt value
 * Simply checks if the excerpt contains "BLOG" (case-insensitive)
 * 
 * To mark as blog: Include "BLOG" anywhere in the excerpt field
 * To mark as article: Don't include "BLOG" in the excerpt, or include "ARTICLE"
 * 
 * Examples:
 * - "BLOG: My personal experience..." → Blog
 * - "[BLOG] Thoughts on radiology..." → Blog
 * - "This is a blog post about..." → Blog
 * - "ARTICLE: Research findings..." → Article
 * - "Clinical study on..." → Article (default)
 */
const isBlog = (excerpt: string | null | undefined): boolean => {
    if (!excerpt) return false;
    
    // Simple check: if excerpt contains "BLOG" (case-insensitive), it's a blog
    return excerpt.toLowerCase().includes('blog');
};

export default function Articles() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [allArticles, setAllArticles] = useState<Article[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [contentType, setContentType] = useState<ContentType>('all');

    const fetchArticles = async () => {
        try {
            setLoading(true);
            // Fetch a large batch to filter client-side
            const res = await getPublishedArticles(1, 1000);
            setAllArticles(res.articles || []);
            setTotal(res.totalItems);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    // Filter articles based on content type
    const filteredArticles = useMemo(() => {
        if (contentType === 'all') {
            return allArticles;
        } else if (contentType === 'blogs') {
            return allArticles.filter(article => isBlog(article.excerpt));
        } else {
            // contentType === 'articles'
            return allArticles.filter(article => !isBlog(article.excerpt));
        }
    }, [allArticles, contentType]);

    // Paginate filtered articles
    const paginatedArticles = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredArticles.slice(start, end);
    }, [filteredArticles, page, rowsPerPage]);

    const filteredTotal = filteredArticles.length;

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleContentTypeChange = (_: React.SyntheticEvent, newValue: ContentType) => {
        setContentType(newValue);
        setPage(0); // Reset to first page when changing filter
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Seo
                title="Radiology Articles and Blogs"
                description="Read educational radiology articles and blogs with teaching notes, figures, case context, and visual learning guidance."
                canonicalPath="/articles"
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 2, sm: 3 } }}>
                <Typography 
                    variant="h3" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 700,
                        fontSize: {
                            xs: '1.5rem',
                            sm: '2rem',
                            md: '2.5rem',
                        }
                    }}
                >
                    Articles & Blogs
                </Typography>
            </Stack>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900, lineHeight: 1.8, mb: 3 }}>
                Browse written radiology learning material that adds context around illustrations, imaging signs, anatomy, and diagnostic reasoning. Articles are intended for education, revision, and teaching support.
            </Typography>

            {/* Filter Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 }, overflowX: 'auto' }}>
                <Tabs 
                    value={contentType} 
                    onChange={handleContentTypeChange} 
                    aria-label="content type tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': {
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            minWidth: { xs: 80, sm: 120 },
                            px: { xs: 1, sm: 2 },
                        }
                    }}
                >
                    <Tab label={`All (${allArticles.length})`} value="all" />
                    <Tab label={`Articles (${allArticles.filter(a => !isBlog(a.excerpt)).length})`} value="articles" />
                    <Tab label={`Blogs (${allArticles.filter(a => isBlog(a.excerpt)).length})`} value="blogs" />
                </Tabs>
            </Box>

            <ResourceLearningSummary variant="articles" />

            {loading && <Typography>Loading articles...</Typography>}

            {!loading && paginatedArticles.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        {contentType === 'all' 
                            ? 'No articles published yet.'
                            : contentType === 'blogs'
                            ? 'No blogs found.'
                            : 'No articles found.'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto', mt: 1, lineHeight: 1.7 }}>
                        New educational articles are added as they are prepared and reviewed. In the meantime, explore the illustration and infographic libraries for visual radiology learning resources.
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {paginatedArticles.map((article) => (
                    <Grid key={article.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Card 
                            sx={{ 
                                height: "100%", 
                                display: "flex", 
                                flexDirection: "column",
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 3
                                }
                            }}
                        >
                            {article.coverImage && (
                                <CardMedia 
                                    component="img" 
                                    image={article.coverImage} 
                                    alt={article.title} 
                                    sx={{ height: 200, objectFit: "cover" }} 
                                />
                            )}
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        mb: 1,
                                        fontSize: {
                                            xs: '1rem',
                                            sm: '1.25rem',
                                        },
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {article.title}
                                </Typography>
                                {article.excerpt && (
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                            mb: 2, 
                                            flex: 1,
                                            fontSize: {
                                                xs: '0.8rem',
                                                sm: '0.875rem',
                                            },
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {article.excerpt}
                                    </Typography>
                                )}
                                <Stack 
                                    direction="row" 
                                    spacing={1} 
                                    alignItems="center" 
                                    sx={{ mt: 'auto' }}
                                    flexWrap="wrap"
                                >
                                    <Chip 
                                        label={isBlog(article.excerpt) ? "Blog" : "Article"} 
                                        color={isBlog(article.excerpt) ? "primary" : "success"} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                    />
                                    {article.publishedAt && (
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary"
                                            sx={{ 
                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {formatDate(article.publishedAt)}
                                        </Typography>
                                    )}
                                </Stack>
                            </CardContent>
                            <Box sx={{ p: 2, pt: 0 }}>
                                <Button 
                                    variant="outlined" 
                                    fullWidth
                                    component={Link}
                                    to={`/articles/${article.slug}`}
                                >
                                    Read More
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {filteredTotal > 0 && (
                <TablePagination
                    component="div"
                    count={filteredTotal}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                />
            )}
        </Box>
    );
}
